import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SmsIcon from '@mui/icons-material/Sms';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import firebase from '../utilities/firebase';
import { StateManager } from '../utilities/stateManager';
import RequestManager from '../utilities/requestManager';

const ITEM_LABELS = { sales: 'Sales Deposit', service: 'Service Payment', shipping: 'Shipping Payment' };

// Shared "Request Payment" control: generates a Stripe payment link for the given
// customer/reference and lets the back office open, copy, or text it to the customer
// so they can pay themselves. The generated intent uses setup_future_usage so the
// card is saved to the customer for later back-office charges.
export default function RequestPaymentButton({
  customer = {},
  reference,
  type = 'sales',
  title = '',
  thumbnail = '',
  defaultAmount = 0,
  reason,
  itemLabel,
  label = 'Request Payment',
}) {
  const [expanded, setExpanded] = React.useState(false);
  const [amount, setAmount] = React.useState(defaultAmount);
  const [inputValue, setInputValue] = React.useState(defaultAmount > 0 ? String(defaultAmount) : '');
  const [paymentLink, setPaymentLink] = React.useState(null);
  const generatingRef = React.useRef(null);
  const devtype = process.env.NODE_ENV === 'development' ? 'dev' : 'prod';

  const customerName = customer.customerName || `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
  const linked = !!customer.id;
  const cartLabel = itemLabel || ITEM_LABELS[type] || 'Payment';

  const generateLink = async () => {
    if (paymentLink) return paymentLink;
    if (generatingRef.current) return await generatingRef.current;
    if (amount < 1) { StateManager.setAlertAndOpen('Amount is too low.', 'error'); return null; }
    if (!customerName) { StateManager.setAlertAndOpen('Customer is missing information.', 'error'); return null; }

    const promise = (async () => {
      StateManager.setLoading(true);
      let cust_id = {};
      if (!customer.stripe_id) {
        const res = await RequestManager.post({
          function: 'makeStripeCustomer',
          variables: { params: { email: customer.email, name: customerName, devtype } },
        });
        if (res.success !== false) {
          if (customer.id) await firebase.firestore().doc(`customers/${customer.id}`).update({ stripe_id: res.id });
          cust_id = { customer: res.id };
        }
      } else {
        cust_id = { customer: customer.stripe_id };
      }

      const intent = await RequestManager.post({
        function: 'makePaymentIntent',
        variables: {
          params: {
            amount: Math.floor(amount * 100),
            ...cust_id,
            setup_future_usage: 'off_session',
            receipt_email: customer.email || null,
            metadata: { reference, type },
            description: `${reference} | ${title} | ${type}`,
            devtype,
          },
          options: {},
        },
      });

      if (!intent.client_secret) {
        StateManager.setLoading(false);
        generatingRef.current = null;
        StateManager.setAlertAndOpen('Error generating link.', 'error');
        return null;
      }

      const link = `${window.location.origin}/payment?intent=${intent.client_secret}&stock=${reference}&type=${type}&thumbnail=${thumbnail || ''}&title=${encodeURIComponent(title)}&i=${encodeURIComponent(cartLabel)}&c=${amount}`;
      setPaymentLink(link);
      generatingRef.current = null;
      StateManager.setLoading(false);
      return link;
    })();

    generatingRef.current = promise;
    return await promise;
  };

  const openLink = async () => { const l = await generateLink(); if (l) window.open(l, '_blank'); };
  const copyLink = async () => {
    const l = await generateLink();
    if (l) { await navigator.clipboard.writeText(l); StateManager.setAlertAndOpen('Link copied!', 'success'); }
  };
  const sendSms = async () => {
    if (!customer.phone_number) { StateManager.setAlertAndOpen('Customer needs a phone number.', 'error'); return; }
    if (!customer.email) { StateManager.setAlertAndOpen('Customer needs an email.', 'error'); return; }
    const l = await generateLink();
    if (!l) return;
    StateManager.setLoading(true);
    const sms = await RequestManager.post({
      function: 'sendPaymentLink',
      variables: {
        params: {
          name: customer.first_name,
          phone_number: customer.phone_number.replace(/\D+/g, ''),
          reason: reason || `Please use this link to pay your ${type} balance${title ? ` on the ${title}` : ''}`,
          link: l,
          devtype,
        },
        options: {},
      },
    });
    StateManager.setLoading(false);
    StateManager.setAlertAndOpen(sms.success !== false ? 'Link sent!' : 'Error sending link.', sms.success !== false ? 'success' : 'error');
  };

  if (!linked) {
    return (
      <Tooltip title="Link a customer to enable payment links">
        <span>
          <Button size="small" variant="outlined" color="secondary" startIcon={<AttachMoneyIcon />} disabled>
            {label}
          </Button>
        </span>
      </Tooltip>
    );
  }

  if (!expanded) {
    return (
      <Button size="small" variant="outlined" color="secondary" startIcon={<AttachMoneyIcon />} onClick={() => setExpanded(true)}>
        {label}
      </Button>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
      <TextField
        size="small"
        label="Amount"
        value={`$${inputValue}`}
        onChange={(e) => {
          const raw = e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
          setInputValue(raw);
          const v = parseFloat(raw);
          setAmount(isNaN(v) ? 0 : v);
          setPaymentLink(null);
          generatingRef.current = null;
        }}
        sx={{ width: 110 }}
        inputProps={{ style: { textAlign: 'center' } }}
      />
      <IconButton size="small" color="primary" onClick={openLink} disabled={amount < 1} title="Open link">
        <OpenInNewIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <IconButton size="small" color="primary" onClick={copyLink} disabled={amount < 1} title="Copy link">
        <ContentCopyIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <IconButton size="small" color="secondary" onClick={sendSms} disabled={amount < 1} title="Send SMS">
        <SmsIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
}
